/**
 * User suspension state types and the canonical `formatSuspensions` reducer.
 *
 * Each suspension is an append-only `SuspensionObject` recording warn/unwarn or
 * block/unblock plus who did it and why; the active state is derived by sorting
 * and looking at the most recent action. `formatSuspensions` is the canonical
 * reducer — it sorts, separates blocks from warnings, and computes "active
 * warnings" by grouping per-comment and keeping only the ones whose latest
 * action is `WARNED`. The read hooks and write helpers that reach the server
 * live in `data/suspensions`.
 */

export enum SuspensionStatusType {
    WARNED = 'warned',
    UNWARNED = 'unwarned',
    BLOCKED = 'blocked',
    UNBLOCKED = 'unblocked',
}

export type SuspensionObject = {
    // Unique key
    key: string;
    // Time of creation
    time: number;
    // Reciver of suspension
    recipientKey: string;
    // Name of the moderator
    blockedByName?: string | null;
    // What is the current state
    suspensionType: SuspensionStatusType;
    // When does it expires
    expiresAt?: number;
    // Key of the moderator
    from?: string | null;

    // -------------------------> In case it's attached to an object, all should
    // be set Question / Article Key
    instanceKey?: string;
    // Structure (question, topic etc.)
    structureKey?: string;
    // Comment key
    objectKey?: string;
    // Type of the object, currently only comment
    objectType?: string;
};

export type FormattedSuspensions = {
    all: SuspensionObject[];
    blocks: SuspensionObject[];
    warnings: SuspensionObject[];
    activeWarnings: SuspensionObject[];
    latestBlock: SuspensionObject | null;
};

// filter and sort all suspensions into an easily readable format
export function formatSuspensions(suspensionMap: Record<string, SuspensionObject>): FormattedSuspensions {
    let suspensionsList = suspensionMap ? Object.values(suspensionMap) : [];
    suspensionsList = suspensionsList.toSorted((a, b) => b.time - a.time);

    const blocksAndUnblocks = suspensionsList.filter(
        (s) => s.suspensionType === SuspensionStatusType.BLOCKED || s.suspensionType === SuspensionStatusType.UNBLOCKED,
    );
    const warnings = suspensionsList.filter(
        (s) => s.suspensionType === SuspensionStatusType.WARNED || s.suspensionType === SuspensionStatusType.UNWARNED,
    );

    return {
        all: suspensionsList,
        blocks: blocksAndUnblocks,
        warnings,
        activeWarnings: getActiveWarnings(suspensionsList),
        latestBlock: blocksAndUnblocks.length > 0 ? blocksAndUnblocks[0] : null,
    };
}

function getActiveWarnings(suspensions: SuspensionObject[]): SuspensionObject[] {
    const sortedSuspensions = suspensions.toSorted((a, b) => b.time - a.time);
    const warnings = sortedSuspensions.filter(
        (s) => s.suspensionType === SuspensionStatusType.WARNED || s.suspensionType === SuspensionStatusType.UNWARNED,
    );
    // Determine which warnings are currently active. A warning is "active" if
    // the most recent warning action for that comment was WARNED (not
    // UNWARNED).
    const warningsByCommentKey = new Map<string, SuspensionObject[]>();
    // Group all warnings by the comment they apply to
    for (const warning of warnings) {
        if (!warning.objectKey) continue;
        if (!warningsByCommentKey.has(warning.objectKey)) {
            warningsByCommentKey.set(warning.objectKey, []);
        }
        warningsByCommentKey.get(warning.objectKey)?.push(warning);
    }
    // For each comment, check if the most recent action was "warn" or "unwarn"
    const activeWarnings: SuspensionObject[] = [];
    for (const commentWarnings of warningsByCommentKey.values()) {
        const mostRecentAction = commentWarnings[0];

        if (mostRecentAction.suspensionType === SuspensionStatusType.WARNED) {
            activeWarnings.push(mostRecentAction);
        }
    }
    return activeWarnings;
}
